/**
 * Форма объявления о грузе — создание и правка, блок `WEB-2.1`, требования
 * 301, 302 (правка), 306, 314, 315 и 318.
 *
 * Форма отправляет **ровно** поля `CargoListingInput` и ничего сверх, а кузов —
 * ровно поля `CargoBodyInput`. Поле формы названо именем поля тела
 * (`weight_kg`, `body.length_m`): тем же именем сервер называет поле в отказе,
 * и его сообщение ложится под своим полем единственным механизмом
 * `renderFieldMessages`; сообщение о поле, которого на форме нет, — общим
 * текстом над формой.
 *
 * Чего форма **не** делает, названо здесь же, потому что соблазн каждого — в
 * одну строку. Километраж, страны транзита и число границ считает сервер и
 * отдаёт карточкой созданного объявления; предварительного числа в форме нет
 * (решение `Р4` А). Даты, пределы, обязательность полей и дней отсрочки судит
 * сервер (`422` под полем): поле «дни отсрочки» видно при любом моменте оплаты,
 * пустое уходит `null` (решение формы `Б10`). Какие параметры кузова применимы
 * к типу, говорит справочник `listBodyTypes` — своей таблицы «тип →
 * параметры» у клиента нет, и смена типа убирает поля прежнего: их значения не
 * отправляются. Торг — переключатель, в новой форме он стоит в «торг
 * разрешён» и уходит строгим логическим (требование 318).
 *
 * Создание несёт `Idempotency-Key` (решение формы `Б5`): повтор отправки до
 * ответа — тот же ключ, а повтор, пока запрос в пути, вовсе не уходит; новый
 * ключ — после ответа сервера или после правки поля. Обрыв связи ответа не
 * дал, и ключ остаётся: сервер, возможно, объявление уже завёл. Правка ключа
 * не несёт (поправка CONTRACT `К6`). Отправка только онлайн (решение `Б6`).
 *
 * Смена языка не стирает введённое (долг `TD-72`): оболочка не пересобирает
 * экран, а переподписывает его — поля и значения остаются теми же узлами.
 *
 * Поля, общие с внутренним заказом, и переподпись — `cargo-form-parts.ts` (блок
 * `WEB-3.5`, решение `Б4`); здесь условия объявления и отправка.
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient, CargoListingSubmission } from '../api/client';
import type { CargoListing } from '../api/contract';
import {
  CURRENCIES,
  PAYMENT_METHODS,
  PAYMENT_MOMENTS,
} from '../api/generated/enums';
import {
  currencyStringKey,
  paymentMethodStringKey,
  paymentMomentStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { navigate } from '../router';
import { createCargoFormParts, decimalOf, integerOf, pick, showFormRefusal, textOf } from './cargo-form-parts';
import {
  placeState,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface CargoListingFormScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  /** Объявление правки; без него — форма новой записи. */
  readonly listingId?: string;
}

export interface CargoListingFormScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — форма показана. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; введённое остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

export function createCargoListingFormScreen(options: CargoListingFormScreenOptions): CargoListingFormScreen {
  const { client, companyId, listingId } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const notice = document.createElement('p');
  const form = document.createElement('form');
  const submitButton = document.createElement('button');
  const parts = createCargoFormParts({
    client,
    strings,
    weightCaption: (shown) => shown.cargo_listing_form_weight,
  });
  const { caption, labelled, input, checkbox, enumSelect } = parts;

  element.className = 'screen screen-cargo-listing-form';
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  form.className = 'form';
  form.noValidate = true;
  submitButton.type = 'submit';
  submitButton.className = 'action action--primary';

  // ── Поля формы ──────────────────────────────────────────────────────────

  const vehiclesCount = input('vehicles_count', 'number');
  const priceAmount = input('price_amount', 'text');
  const currency = enumSelect('price_currency', CURRENCIES, currencyStringKey);
  const negotiable = checkbox('negotiable', () => strings.cargo_listing_form_negotiable);
  const paymentMethod = enumSelect('payment_method', PAYMENT_METHODS, paymentMethodStringKey);
  const paymentMoment = enumSelect('payment_moment', PAYMENT_MOMENTS, paymentMomentStringKey);
  const deferralDays = input('payment_deferral_days', 'number');

  priceAmount.inputMode = 'decimal';
  // Новая форма стоит в «торг разрешён» (требование 318); правка — значением
  // объявления.
  negotiable.toggle.checked = true;

  caption(() => {
    submitButton.textContent = listingId === undefined
      ? strings.cargo_listing_form_submit
      : strings.cargo_listing_form_save;
    heading.textContent = listingId === undefined
      ? strings.cargo_listing_form_title_new
      : strings.cargo_listing_form_title_edit;
  });

  form.append(
    parts.origin.label,
    parts.destination.label,
    parts.loadingAtField,
    parts.unloadByField,
    labelled(vehiclesCount, () => strings.cargo_listing_form_vehicles_count),
    parts.cargoTypeField,
    parts.weightField,
    parts.adrField,
    parts.bodyTypeField,
    parts.bodyArea,
    labelled(priceAmount, () => strings.cargo_listing_form_price),
    labelled(currency, () => strings.cargo_listing_form_currency),
    negotiable.label,
    labelled(paymentMethod, () => strings.cargo_listing_form_payment_method),
    labelled(paymentMoment, () => strings.cargo_listing_form_payment_moment),
    labelled(deferralDays, () => strings.cargo_listing_form_deferral_days),
    parts.commentField,
    submitButton,
  );

  /** Тело — ровно поля формы контракта; пустое поле уходит `null`. */
  function entered(): CargoListingSubmission {
    return {
      origin_city_id: integerOf(parts.origin.select.value),
      destination_city_id: integerOf(parts.destination.select.value),
      loading_at: textOf(parts.loadingAt.value),
      unload_by: textOf(parts.unloadBy.value),
      vehicles_count: integerOf(vehiclesCount.value),
      cargo_type: textOf(parts.cargoType.value),
      weight_kg: integerOf(parts.weight.value),
      adr: parts.adr.checked,
      body: parts.enteredBody(),
      price_amount: decimalOf(priceAmount.value),
      price_currency: pick(CURRENCIES, currency.value),
      negotiable: negotiable.toggle.checked,
      payment_method: pick(PAYMENT_METHODS, paymentMethod.value),
      payment_moment: pick(PAYMENT_MOMENTS, paymentMoment.value),
      payment_deferral_days: integerOf(deferralDays.value),
      comment: textOf(parts.comment.value),
    };
  }

  /** Правка: форма заполняется значениями объявления из ответа сервера. */
  function fillFrom(listing: CargoListing): void {
    parts.chooseCity(parts.origin, listing.origin_city);
    parts.chooseCity(parts.destination, listing.destination_city);
    parts.loadingAt.value = listing.loading_at;
    parts.unloadBy.value = listing.unload_by;
    vehiclesCount.value = String(listing.vehicles_count);
    parts.cargoType.value = listing.cargo_type;
    parts.weight.value = String(listing.weight_kg);
    parts.adr.checked = listing.adr;
    parts.fillBody(listing.body);
    priceAmount.value = listing.price === null ? '' : listing.price.amount;
    currency.value = listing.price === null ? '' : listing.price.currency;
    negotiable.toggle.checked = listing.negotiable;
    paymentMethod.value = listing.payment.method;
    paymentMoment.value = listing.payment.moment;
    deferralDays.value = listing.payment.deferral_days === null ? '' : String(listing.payment.deferral_days);
    parts.comment.value = listing.comment ?? '';
  }

  // ── Состояние экрана и отправка ─────────────────────────────────────────

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — справочник кузовов (и объявление правки). */
  let answered = false;
  /** Запрос в пути: повтор отправки до ответа не уходит вторым запросом. */
  let sending = false;
  /** Ключ идемпотентности текущей отправки создания либо `null`. */
  let idempotencyKey: string | null = null;

  function render(): void {
    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // справочник не пришёл — формы без него нет, и состояние встаёт на место
    // экрана; пришёл — форма на месте, общий отказ ложится над ней.
    placeState(element, { state, answered, frame: [heading, notice], list: form }, strings);
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    render();

    try {
      if (listingId === undefined) {
        parts.setBodyTypes(await client.listBodyTypes());
      } else {
        const [types, listing] = await Promise.all([client.listBodyTypes(), client.getCargoListing(listingId)]);

        parts.setBodyTypes(types);
        fillFrom(listing);
      }

      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function send(): Promise<void> {
    sending = true;
    submitButton.disabled = true;
    notice.textContent = '';
    renderFieldMessages(form, []);

    const body = entered();

    try {
      let saved: CargoListing;

      if (listingId === undefined) {
        idempotencyKey ??= newIdempotencyKey();
        saved = await client.createCargoListing(companyId, body, idempotencyKey);
      } else {
        saved = await client.updateCargoListing(listingId, body);
      }

      idempotencyKey = null;
      // Созданное и исправленное открываются карточкой: километраж, транзит и
      // доску она перечитывает у сервера, а не дописывает клиентом.
      navigate({ kind: 'cargoListing', companyId, listingId: saved.id });
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;

      if (failure !== null && failure.code !== null) {
        // Сервер ответил — следующая отправка новая и несёт новый ключ.
        idempotencyKey = null;
      }

      showFormRefusal(form, notice, error, strings);
    } finally {
      sending = false;
      submitButton.disabled = false;
    }
  }

  // Правка любого поля — новая отправка: ключ прежней снимается (решение `Б5`).
  form.addEventListener('input', () => {
    idempotencyKey = null;
  });
  form.addEventListener('change', () => {
    idempotencyKey = null;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    void send();
  });

  function relabel(next: Strings): void {
    strings = next;
    parts.relabel(next);
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
